import { safeAwait, safeJsonFetch, safeTimeout, type SafePromise, type SafeType } from '@benzinga/safe-await';
import { logger } from '@benzinga/logging';
import type { Session } from '@benzinga/session';
import { TtlFifoCache } from '@benzinga/utils';
import { ChartbeatParams, ChartbeatPost, News } from './entities';
import { BasicNewsEnvironment } from './environment';
import { BasicNewsManager } from './manager';

export interface ChartbeatResponse {
  pages: ChartbeatPost[];
}

export interface ChartbeatLink {
  title: string;
  url: string;
}

export interface GetTopPostsUsingChartbeatInternalConfig {
  source?: string;
}

const ALLOWED_PARAMS = new Set(['host', 'limit', 'section', 'tag', 'type', 'types']);
const DEFAULT_HOST = 'benzinga.com';
const DEFAULT_LIMIT = '10';
const CACHE_TTL_MS = 120_000;
const STALE_FALLBACK_MAX_MS = 86_400_000;
const DEFAULT_TIMEOUT_MS = 3000;
const FETCH_TIMEOUT_MS = 2000;
const DEFAULT_HYDRATE_LIMIT = 4;
const MAX_CACHE_ENTRIES = 100;

const chartbeatCache = new TtlFifoCache(MAX_CACHE_ENTRIES);
const lastGoodByKey = new Map<string, { data: ChartbeatResponse; storedAt: number }>();

const hydratedCache = new TtlFifoCache(MAX_CACHE_ENTRIES);
const hydratedInFlight = new Map<string, SafePromise<News[]>>();
const hydratedLastGood = new Map<string, { data: News[]; storedAt: number }>();

function appendAllowedParams(
  filteredParams: URLSearchParams,
  params: URLSearchParams | ChartbeatParams,
  allowedParams: ReadonlySet<string>,
) {
  const entries =
    params instanceof URLSearchParams
      ? params.entries()
      : Object.entries(params).filter((entry): entry is [string, string] => entry[1] !== undefined && entry[1] !== '');

  for (const [key, value] of entries) {
    if (!allowedParams.has(key)) continue;
    filteredParams.set(key, String(value).toLowerCase());
  }
}

function normalizeChartbeatParams(params: URLSearchParams | ChartbeatParams = {}): URLSearchParams {
  const filteredParams = new URLSearchParams();
  appendAllowedParams(filteredParams, params, ALLOWED_PARAMS);

  if (!filteredParams.has('host')) filteredParams.set('host', DEFAULT_HOST);
  if (!filteredParams.has('limit')) filteredParams.set('limit', DEFAULT_LIMIT);

  return filteredParams;
}

function getCacheKey(params: URLSearchParams): string {
  return Array.from(params.entries())
    .sort(([firstKey], [secondKey]) => firstKey.localeCompare(secondKey))
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
}

function getHydratedCacheKey(params: URLSearchParams, hydrateLimit: number): string {
  return `${getCacheKey(params)}&hydrate_limit=${hydrateLimit}`;
}

function getHydrateLimit(params: ChartbeatParams): number {
  return params?.limit ?? DEFAULT_HYDRATE_LIMIT;
}

const ARTICLE_PATH_STORY_ID_REGEX = /[0-9]{2}\/[0-9]{2}\/([0-9]+)/;

function extractStoryIdFromPage(page: ChartbeatPost): string | undefined {
  return page.path?.match(ARTICLE_PATH_STORY_ID_REGEX)?.[1];
}

function filterArticleChartbeatPages(pages: ChartbeatPost[]): ChartbeatPost[] {
  return pages.filter(page => extractStoryIdFromPage(page) !== undefined);
}

function chartbeatPostToUrl(post: ChartbeatPost): string {
  if (!post.path) return 'https://www.benzinga.com';
  const path = post.path.startsWith('/') ? post.path : `/${post.path}`;
  return `https://www.benzinga.com${path}`;
}

function chartbeatPagesToLinks(pages: ChartbeatPost[], limit: number): ChartbeatLink[] {
  return filterArticleChartbeatPages(pages)
    .slice(0, limit)
    .map(page => ({
      title: page.title ?? '',
      url: chartbeatPostToUrl(page),
    }));
}

function extractStoryIdsFromPages(pages: ChartbeatPost[], limit: number): number[] {
  const topPostIds = filterArticleChartbeatPages(pages)
    .map(page => extractStoryIdFromPage(page))
    .filter((id): id is string => id !== undefined)
    .map(id => Number(id));

  return [...new Set(topPostIds)].slice(0, limit);
}

function copyChartbeatResponse(response: ChartbeatResponse): ChartbeatResponse {
  return { pages: response.pages.slice() };
}

async function getCachedChartbeatPages(
  cacheKey: string,
  request: () => Promise<ChartbeatResponse>,
): Promise<ChartbeatResponse> {
  const cachedResponse = chartbeatCache.get<ChartbeatResponse>(cacheKey);
  if (cachedResponse !== undefined) return copyChartbeatResponse(cachedResponse);

  const response = await request();
  chartbeatCache.set(cacheKey, response, CACHE_TTL_MS);
  lastGoodByKey.set(cacheKey, { data: response, storedAt: Date.now() });
  return copyChartbeatResponse(response);
}

function getStaleChartbeatPages(cacheKey: string): ChartbeatResponse | undefined {
  const lastGood = lastGoodByKey.get(cacheKey);
  if (!lastGood) return undefined;
  if (Date.now() - lastGood.storedAt >= STALE_FALLBACK_MAX_MS) return undefined;
  return lastGood.data;
}

function getStaleHydratedNews(cacheKey: string): News[] | undefined {
  const lastGood = hydratedLastGood.get(cacheKey);
  if (!lastGood) return undefined;
  if (Date.now() - lastGood.storedAt >= STALE_FALLBACK_MAX_MS) return undefined;
  return lastGood.data;
}

// hydratedCache and in-flight dedupe share one canonical News[] per key; callers such as
// apps/bz/app/crypto/utils.ts mutate the returned array (.pop / .push), so each caller needs its own copy.
function copyHydratedNewsResult(result: SafeType<News[]>): SafeType<News[]> {
  return result.ok ? { ok: result.ok.slice() } : result;
}

/**
 * Server-side only: fetch Chartbeat top pages directly (no self-HTTP to /api/chartbeat).
 */
export async function getChartbeatTopPages(
  params: URLSearchParams | ChartbeatParams = {},
  options: { apiKey: string; timeoutMs?: number },
): Promise<ChartbeatResponse> {
  const filteredParams = normalizeChartbeatParams(params);
  const cacheKey = getCacheKey(filteredParams);
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  const apiUrl = new URL('https://api.chartbeat.com/live/toppages/v3');
  apiUrl.search = filteredParams.toString();

  try {
    return await getCachedChartbeatPages(cacheKey, async () => {
      const result = await safeTimeout(
        safeJsonFetch<ChartbeatResponse>(apiUrl.toString(), {
          headers: { 'X-CB-AK': options.apiKey },
        }),
        timeoutMs,
      );
      if (result.err) throw result.err;
      return result.ok ?? { pages: [] };
    });
  } catch (error) {
    const stale = getStaleChartbeatPages(cacheKey);
    if (stale) return copyChartbeatResponse(stale);
    throw error;
  }
}

async function fetchHydratedChartbeatNews(
  session: Session,
  params: ChartbeatParams,
  config: GetTopPostsUsingChartbeatInternalConfig,
): SafePromise<News[]> {
  const apiKey = session.getEnvironment(BasicNewsEnvironment).chartbeatKey;
  const hydrateLimit = getHydrateLimit(params);

  const chartbeatResponse = await safeAwait(getChartbeatTopPages(params, { apiKey, timeoutMs: FETCH_TIMEOUT_MS }));
  if (chartbeatResponse.err) {
    if (config.source) {
      logger.error(`[${config.source}] Chartbeat top pages failed`, chartbeatResponse.err, {
        event: 'chartbeat_top_pages_failed',
      });
    }
    return { err: chartbeatResponse.err };
  }

  const pages = chartbeatResponse.ok?.pages;
  if (!Array.isArray(pages)) {
    return { ok: [] };
  }

  const ids = extractStoryIdsFromPages(pages, hydrateLimit);
  if (ids.length === 0) {
    return { ok: [] };
  }

  const storiesResult: SafeType<News[]> = await safeTimeout(
    session.getManager(BasicNewsManager).getStoriesWithIds(ids),
    FETCH_TIMEOUT_MS,
  );
  if (storiesResult.err && config.source) {
    logger.warn(`[${config.source}] Chartbeat top posts stories fetch failed`, storiesResult.err, {
      event: 'chartbeat_stories_fetch_failed',
    });
  }
  return storiesResult;
}

/**
 * Server-side only: Chartbeat top pages as title/url links (no story hydration).
 */
export async function getChartbeatLinksInternal(
  session: Session,
  params: ChartbeatParams = {},
  config: GetTopPostsUsingChartbeatInternalConfig = {},
): SafePromise<ChartbeatLink[]> {
  const apiKey = session.getEnvironment(BasicNewsEnvironment).chartbeatKey;
  const hydrateLimit = getHydrateLimit(params);

  const chartbeatResponse = await safeAwait(getChartbeatTopPages(params, { apiKey, timeoutMs: FETCH_TIMEOUT_MS }));
  if (chartbeatResponse.err) {
    if (config.source) {
      logger.error(`[${config.source}] Chartbeat top pages failed`, chartbeatResponse.err, {
        event: 'chartbeat_top_pages_failed',
      });
    }
    return { err: chartbeatResponse.err };
  }

  const pages = chartbeatResponse.ok?.pages;
  if (!Array.isArray(pages)) {
    return { ok: [] };
  }

  return { ok: chartbeatPagesToLinks(pages, hydrateLimit) };
}

/**
 * Server-side only: Chartbeat top pages resolved to full news records (no self-HTTP to /api/chartbeat).
 */
export async function getTopPostsUsingChartbeatInternal(
  session: Session,
  params: ChartbeatParams = {},
  config: GetTopPostsUsingChartbeatInternalConfig = {},
): SafePromise<News[]> {
  const filteredParams = normalizeChartbeatParams(params);
  const hydrateLimit = getHydrateLimit(params);
  const cacheKey = getHydratedCacheKey(filteredParams, hydrateLimit);

  const cached = hydratedCache.get<News[]>(cacheKey);
  if (cached !== undefined) {
    return { ok: cached.slice() };
  }

  const inFlight = hydratedInFlight.get(cacheKey);
  if (inFlight) {
    return inFlight.then(copyHydratedNewsResult);
  }

  const request = fetchHydratedChartbeatNews(session, params, config)
    .then(result => {
      if (result.ok) {
        hydratedCache.set(cacheKey, result.ok, CACHE_TTL_MS);
        hydratedLastGood.set(cacheKey, { data: result.ok, storedAt: Date.now() });
        return copyHydratedNewsResult(result);
      }
      const stale = getStaleHydratedNews(cacheKey);
      if (stale) {
        if (config.source) {
          logger.warn(`[${config.source}] Chartbeat hydrated stories stale fallback`, result.err, {
            event: 'chartbeat_stale_fallback',
          });
        }
        return { ok: stale.slice() };
      }
      return result;
    })
    .finally(() => hydratedInFlight.delete(cacheKey));

  hydratedInFlight.set(cacheKey, request);
  return request;
}
